import { useCallback, useMemo } from "react";
import type { ApiRequestOptions, ApiResponse, UseApiClientReturn } from "@Front/Hooks/useApiClientTypes";

type SendRequestFn = <T = unknown>(
	method: "GET" | "POST" | "PUT" | "DELETE",
	endpoint: string,
	body?: unknown,
	options?: ApiRequestOptions,
) => Promise<ApiResponse<T>>;

export function useApiCrudVerbs(sendRequest: SendRequestFn): Pick<UseApiClientReturn, "get" | "post" | "put" | "delete"> {
	const get = useCallback(
		async <T = unknown>(endpoint: string, options?: ApiRequestOptions): Promise<ApiResponse<T>> => {
			return await sendRequest<T>("GET", endpoint, undefined, options);
		},
		[sendRequest],
	);

	const post = useCallback(
		async <T = unknown>(endpoint: string, body?: unknown, options?: ApiRequestOptions): Promise<ApiResponse<T>> => {
			return await sendRequest<T>("POST", endpoint, body, options);
		},
		[sendRequest],
	);

	const put = useCallback(
		async <T = unknown>(endpoint: string, body?: unknown, options?: ApiRequestOptions): Promise<ApiResponse<T>> => {
			return await sendRequest<T>("PUT", endpoint, body, options);
		},
		[sendRequest],
	);

	const del = useCallback(
		async <T = unknown>(endpoint: string, options?: ApiRequestOptions): Promise<ApiResponse<T>> => {
			return await sendRequest<T>("DELETE", endpoint, undefined, options);
		},
		[sendRequest],
	);

	return useMemo(
		() => ({
			get,
			post,
			put,
			delete: del,
		}),
		[get, post, put, del],
	);
}
